/**
 * Capture / Notepad inbox data layer (backend #1182).
 *
 * Mirrors the app's service convention: thin axios calls on the shared `request`
 * instance (auth + base URL handled there), exposed as react-query v3 hooks.
 * Reads use an infinite query (DRF page pagination); writes invalidate the list.
 */
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query";
import API from "./request";
import { API_PATH, QUERY_KEYS } from "../utils/config";
import { removeCaptureFromPages } from "../pages/Capture/Capture.helpers";

const DEFAULT_PAGE_SIZE = 20;

/**
 * react-query `retry` predicate for capture reads.
 *
 * A 4xx (client error — e.g. the backend #1182 endpoints returning 404 because
 * they are not deployed) is not going to succeed on a retry, so we fire exactly
 * ONE request and surface the error immediately instead of hammering the URL
 * four times over ~7s (react-query's default `retry: 3`). Server errors (5xx)
 * and network failures are transient, so those still recover (up to 2 retries).
 *
 * `count` is react-query's failureCount; a 4xx short-circuits on the first call.
 */
export const captureRetry = (count, err) =>
  count < 2 && !(err?.response?.status >= 400 && err?.response?.status < 500);

/* ----- raw endpoints (axios) ----- */

export const fetchCaptureItems = ({ pageParam = 1, pageSize = DEFAULT_PAGE_SIZE } = {}) =>
  API.get(API_PATH.CAPTURE_ITEMS, {
    params: { page: pageParam, page_size: pageSize },
  }).then((res) => res.data);

/**
 * Wheel-side read (ISP-4068): the captures attached to ONE wheel.
 *
 * Backend adds an optional `?attached_wheel=<id>` filter to the same
 * owner-scoped list endpoint, so a wheel view can read back exactly the items
 * attached to that wheel. Server-side filtering keeps it correct across
 * pagination (client-side filtering of the paged inbox would miss items past
 * page 1). Owner-scoped + IDOR-safe on the backend.
 */
export const fetchWheelCaptureItems = ({
  wheelId,
  pageParam = 1,
  pageSize = DEFAULT_PAGE_SIZE,
} = {}) =>
  API.get(API_PATH.CAPTURE_ITEMS, {
    params: { attached_wheel: wheelId, page: pageParam, page_size: pageSize },
  }).then((res) => res.data);

export const createCaptureItem = (payload) =>
  API.post(API_PATH.CAPTURE_ITEMS, payload);

/**
 * Export ALL (C15.13): fetch the caller's entire inbox as one manifest.
 *
 * Owner-scoped + read-only on the backend; returns the success-wrapper body
 * ({ data: { generated_at, count, items } }). Unlike the paginated list read
 * this is a single call that returns every item, so the client can build one
 * downloadable file — see pages/Capture/captureExport.js.
 */
export const fetchCaptureExport = () =>
  API.get(API_PATH.EXPORT_CAPTURE_ITEMS).then((res) => res.data);

/**
 * Edit an existing capture (CAP-3): its note/caption and, for photo/doc, a
 * replacement media_key. PATCH is a partial update -- only send the fields
 * that changed (see Capture.helpers.buildUpdateCapturePayload).
 */
export const updateCaptureItem = (id, payload) =>
  API.patch(API_PATH.UPDATE_CAPTURE_ITEM(id), payload);

export const attachCaptureItem = (id, wheelId) =>
  API.post(API_PATH.ATTACH_CAPTURE_ITEM(id), { wheel_id: wheelId ?? null });

export const deleteCaptureItem = (id) =>
  API.delete(API_PATH.DELETE_CAPTURE_ITEM(id));

/**
 * Delete SEVERAL captures (QA-76/QA-79 bulk delete).
 *
 * The backend exposes no bulk-delete endpoint — `capture/api/viewsets.py` has
 * `DestroyModelMixin` only, one id per request — so this fans out over the
 * endpoint that exists rather than inventing one. `Promise.allSettled`, not
 * `Promise.all`: one row failing (already gone, a transient 5xx) must not
 * abandon the rest of a ten-row selection half-deleted with no report of which
 * half. The caller gets the exact split back so it can tell the user the truth.
 *
 * @param {Array<number|string>} ids
 * @returns {Promise<{deletedIds: Array, failedIds: Array}>}
 */
export const deleteCaptureItems = async (ids = []) => {
  const list = Array.isArray(ids) ? ids : [];
  const settled = await Promise.allSettled(list.map((id) => deleteCaptureItem(id)));
  const deletedIds = [];
  const failedIds = [];
  settled.forEach((result, index) => {
    if (result.status === "fulfilled") deletedIds.push(list[index]);
    else failedIds.push(list[index]);
  });
  return { deletedIds, failedIds };
};

/**
 * Detach a capture from one wheel.
 *
 * POST /capture/items/{id}/detach/  { wheel_id: <number> }
 * Distinct from attach — a null body would clear every M2M link.
 */
export const detachCaptureItem = (id, wheelId) =>
  API.post(API_PATH.DETACH_CAPTURE_ITEM(id), { wheel_id: wheelId });

/**
 * Detach a capture from one or more wheels in one request.
 *
 * POST /capture/items/{id}/detach-wheels/  { wheel_ids: [<number>, ...] }
 */
export const detachCaptureItemWheels = (id, wheelIds = []) =>
  API.post(API_PATH.DETACH_CAPTURE_WHEELS(id), {
    wheel_ids: (Array.isArray(wheelIds) ? wheelIds : [wheelIds])
      .map((wid) => Number(wid))
      .filter((wid) => Number.isFinite(wid)),
  });

/**
 * Detach a capture from ONE specific wheel (ISP-9999 multi-wheel / M2M).
 *
 * Once a capture can be attached to many wheels at once (backend #2470), the
 * legacy "attach with a null wheel" detach is wrong: it clears EVERY attachment,
 * not the one the user picked. This hits the wheel-scoped endpoint, which
 * removes exactly the (item, wheel) pair and preserves the rest.
 *
 * @param {number|string} id       the capture id
 * @param {number|string} wheelId  the single wheel to disconnect from
 */
export const detachCaptureItemFromWheel = (id, wheelId) =>
  API.post(API_PATH.DETACH_CAPTURE_ITEM(id), { wheel_id: wheelId });

/**
 * Detach a capture from SEVERAL wheels (the multi-select "disconnect" path).
 *
 * The backend endpoint takes one wheel_id per request, so this fans out over
 * the endpoint that exists rather than inventing a bulk shape.
 * `Promise.allSettled`, not `Promise.all`: one wheel failing (already detached,
 * a transient 5xx) must not abandon the rest of the selection, and the caller
 * gets the exact split back so it can tell the user the truth about which
 * wheels were disconnected. Wheels NOT in `wheelIds` are never touched — that is
 * what keeps the unselected wheels attached.
 *
 * @param {number|string} id
 * @param {Array<number|string>} wheelIds
 * @returns {Promise<{detachedWheelIds: Array, failedWheelIds: Array}>}
 */
export const detachCaptureItemFromWheels = async (id, wheelIds = []) => {
  const list = (Array.isArray(wheelIds) ? wheelIds : []).filter(
    (w) => w != null
  );
  const settled = await Promise.allSettled(
    list.map((wheelId) => detachCaptureItemFromWheel(id, wheelId))
  );
  const detachedWheelIds = [];
  const failedWheelIds = [];
  settled.forEach((result, index) => {
    if (result.status === "fulfilled") detachedWheelIds.push(list[index]);
    else failedWheelIds.push(list[index]);
  });
  return { detachedWheelIds, failedWheelIds };
};

/* ----- react-query hooks ----- */

export const useCaptureItems = (config = {}) =>
  useInfiniteQuery(
    [QUERY_KEYS.captureItems],
    ({ pageParam = 1 }) => fetchCaptureItems({ pageParam }),
    {
      // DRF returns an absolute `next` URL (or null) — derive the next page number.
      getNextPageParam: (lastPage, pages) =>
        lastPage && lastPage.next ? pages.length + 1 : undefined,
      refetchOnWindowFocus: false,
      retry: captureRetry,
      ...config,
    }
  );

/**
 * Wheel-side read hook (ISP-4068): the captures attached to `wheelId`.
 *
 * Keyed by wheel id so each wheel's attached-capture list caches independently
 * of the main inbox query. Disabled until a wheelId exists (and the caller may
 * gate on the CAPTURE flag / drawer-open via `config.enabled`) so it fires no
 * request while inert. Same infinite-scroll + 4xx-short-circuit retry contract
 * as the inbox read.
 */
export const useWheelCaptureItems = (wheelId, config = {}) =>
  useInfiniteQuery(
    [QUERY_KEYS.captureItems, "wheel", wheelId],
    ({ pageParam = 1 }) => fetchWheelCaptureItems({ wheelId, pageParam }),
    {
      getNextPageParam: (lastPage, pages) =>
        lastPage && lastPage.next ? pages.length + 1 : undefined,
      refetchOnWindowFocus: false,
      retry: captureRetry,
      enabled: !!wheelId,
      ...config,
    }
  );

/**
 * Create-a-capture mutation.
 *
 * `onError` is a DATA-LAYER FLOOR, not the user-facing message: react-query v3
 * runs the hook's callback in addition to any the caller passes to `mutate`, so
 * this deliberately only logs — the composer
 * (`pages/Capture/components/AddCaptureModal.jsx`) owns the toast, and a second
 * toast here would double it. The floor exists so a FUTURE caller that forgets
 * its own `onError` still leaves a trace instead of failing silently (the PR
 * #2449 class). It also gives callers the same `config` passthrough the read
 * hooks already have.
 *
 * Note what this is NOT: it is not a cure for a slow request. A request that is
 * merely pending has neither succeeded nor failed, so no `onError` can fire —
 * the composer's "Loading" state during a 27 s `POST` (measured on `app-stg`
 * 2026-08-03) is the mutation behaving correctly against a slow backend.
 */
export const useCreateCaptureItem = (config = {}) => {
  const queryClient = useQueryClient();
  return useMutation((payload) => createCaptureItem(payload), {
    onSuccess: () => queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
    onError: (err) => console.error("Create capture failed:", err),
    ...config,
  });
};

/**
 * Export-all action (C15.13). A mutation, not a query: it fires only when the
 * user clicks "Export all", and `isLoading` guards the button against a
 * double-submit while the (potentially large) manifest is in flight. The
 * `onError` floor logs; the caller owns the user-facing toast + the download.
 */
export const useCaptureExport = (config = {}) =>
  useMutation(() => fetchCaptureExport(), {
    onError: (err) => console.error("Capture export failed:", err),
    ...config,
  });

export const useUpdateCaptureItem = () => {
  const queryClient = useQueryClient();
  return useMutation(({ id, payload }) => updateCaptureItem(id, payload), {
    onSuccess: () => queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
  });
};

export const useAttachCaptureItem = () => {
  const queryClient = useQueryClient();
  return useMutation(({ id, wheelId }) => attachCaptureItem(id, wheelId), {
    onSuccess: () => queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
  });
};

/**
 * Detach mutation, with an optimistic removal from ONE wheel's attached list.
 *
 * Pass the `wheelId` when detaching from the wheel-side "Captured items on this
 * wheel" drawer: the row (and the header count) update the instant the user
 * confirms instead of after a round-trip, and a failed request rolls the exact
 * previous cache object back so the UI never lies about what is attached.
 * Called from the Capture inbox, pass `{ id, wheelId }` so the body names the
 * wheel being unlinked, or `{ id, wheelIds }` to POST `/detach-wheels/` with
 * that list. The hook's `wheelId` argument is the drawer default
 * (Captured on this wheel) so `mutate(captureId)` still posts that wheel.
 *
 * `onSettled` invalidates the whole `captureItems` key either way, so the inbox
 * list, every per-wheel list, and the badge all reconcile against the server.
 */
export const useDetachCaptureItem = (wheelId) => {
  const queryClient = useQueryClient();
  const wheelKey = [QUERY_KEYS.captureItems, "wheel", wheelId];
  const captureIdOf = (vars) =>
    vars && typeof vars === "object" && "id" in vars ? vars.id : vars;
  const fromWheelOf = (vars) => {
    if (vars && typeof vars === "object" && "wheelId" in vars) return vars.wheelId;
    return wheelId;
  };
  const wheelIdsOf = (vars) =>
    vars && typeof vars === "object" && Array.isArray(vars.wheelIds)
      ? vars.wheelIds
      : null;

  return useMutation((vars) => {
    const id = captureIdOf(vars);
    const ids = wheelIdsOf(vars);
    if (ids) return detachCaptureItemWheels(id, ids);
    return detachCaptureItem(id, fromWheelOf(vars));
  }, {
    onMutate: async (vars) => {
      const id = captureIdOf(vars);
      if (!wheelId) return {};
      // Stop an in-flight refetch from clobbering the optimistic write.
      await queryClient.cancelQueries(wheelKey);
      const previous = queryClient.getQueryData(wheelKey);
      queryClient.setQueryData(wheelKey, (old) =>
        removeCaptureFromPages(old, id)
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      // Roll back to the pre-mutation cache so a failed detach cannot leave the
      // drawer showing an item as gone when the server still has it attached.
      if (context && context.previous !== undefined) {
        queryClient.setQueryData(wheelKey, context.previous);
      }
    },
    onSettled: () => queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
  });
};

/**
 * Wheel-scoped multi-detach mutation (ISP-9999 smart detach).
 *
 * Takes `{ id, wheelIds }` and disconnects the capture from exactly those
 * wheels, leaving every other wheel it is on intact. Invalidates the whole
 * `captureItems` key on settle so the inbox row's attachment chip(s) and every
 * per-wheel list reconcile against the server. No optimistic prune here: the
 * inbox row is not removed by a detach (the capture stays in the inbox), it
 * only loses the wheels it was disconnected from once the list refetches.
 */
export const useDetachCaptureFromWheels = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, wheelIds }) => detachCaptureItemFromWheels(id, wheelIds),
    {
      onSettled: () =>
        queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
    }
  );
};

export const useDeleteCaptureItem = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => deleteCaptureItem(id), {
    onSuccess: () => queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
  });
};

/**
 * Bulk delete (QA-76/QA-79).
 *
 * Invalidates on `onSettled`, not `onSuccess`: a PARTIAL failure still deleted
 * real rows on the server, so the inbox must refetch either way — skipping the
 * refetch on the error path would leave already-deleted rows on screen.
 */
export const useDeleteCaptureItems = () => {
  const queryClient = useQueryClient();
  return useMutation((ids) => deleteCaptureItems(ids), {
    onSettled: () => queryClient.invalidateQueries([QUERY_KEYS.captureItems]),
  });
};
